/**
 * 传承学院 · 三层五维培养矩阵
 *
 * 产品目标：把子女培养这件企业家最焦虑也最无章法的事，变成一张可追踪的矩阵。
 *
 * 与原型的关键差别：
 *   1. 矩阵用 (layerKey, dimKey, value) 三元组表达，页面按 key 查找而不是按下标，
 *      后端将来增删层级/维度不会让整张表错位；
 *   2. 所有数字（矩阵值、完成度、五维分值）均来自接口，页面只负责映射成颜色深浅；
 *   3. 接班人的五维雷达直接取该接班人自己的 dims，不再用「第几个孩子」决定颜色。
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SuccessionOverview } from '@hew3/shared';
import { resolveColor } from '@hew3/shared';
import { api } from '../../lib/api.ts';
import { charts } from '../../lib/charts.ts';
import { formatDate } from '../../lib/format.ts';
import { Badge, Button, Card, Chart, ProgressBar, StateBox } from '../../components/ui.tsx';

/** 层序：接口未下发 order，按常量表 bedrock → forge → strategy 的既定次序排列（基石养成层在前）。 */
const layerOrder = ['bedrock', 'forge', 'strategy'];

/** 目标用户是 50+ 企业家：矩阵里的关键信息不低于 11px。 */
const MATRIX_KEY_MIN_FONT = 11;

/** 四档定位文案：五维得分所处区间的解释性说明，不含数字。 */
function levelOf(value: number): string {
  if (value >= 85) return '标杆水平，可作为同层示范';
  if (value >= 70) return '达标且有优势，按计划推进';
  if (value >= 55) return '基础达标，需在培养计划中加厚';
  return '明显薄弱，建议优先补足';
}

export function SuccessionPage() {
  const navigate = useNavigate();
  const overviewQuery = useQuery({
    queryKey: ['succession'],
    queryFn: () => api.get<SuccessionOverview>('/api/succession/overview'),
  });

  if (overviewQuery.isLoading) {
    return (
      <div className="page-pad">
        <div className="skeleton skel-card" />
        <div className="skeleton skel-card" style={{ height: 236 }} />
        <div className="skeleton skel-card" style={{ height: 200 }} />
        <div className="skeleton skel-card" />
      </div>
    );
  }

  if (overviewQuery.isError || !overviewQuery.data) {
    return (
      <div className="page">
        <div className="scr-head">
          <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
            <svg viewBox="0 0 24 24">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <div className="scr-title">
            传承学院<small>三层五维 · 战略性培养工程</small>
          </div>
        </div>
        <StateBox
          kind="error"
          title="传承数据加载失败"
          desc="请检查网络后重试。若持续失败，可联系专属健康管家协助核对家庭档案。"
          action={
            <Button small onClick={() => void overviewQuery.refetch()}>
              重新加载
            </Button>
          }
        />
      </div>
    );
  }

  const { layers, dimensions, matrix, successors, resources } = overviewQuery.data;

  // 层序按常量表既定次序；未登记的层排到最后，不因排序丢失数据。
  const orderedLayers = [...layers].sort((a, b) => {
    const ia = layerOrder.indexOf(a.key);
    const ib = layerOrder.indexOf(b.key);
    return (ia < 0 ? layerOrder.length : ia) - (ib < 0 ? layerOrder.length : ib);
  });

  // (layerKey, dimKey) → value：按 key 查找，绝不依赖数组下标。
  const cellValue = new Map<string, number>();
  for (const cell of matrix) cellValue.set(`${cell.layerKey}|${cell.dimKey}`, cell.value);

  const valueOf = (layerKey: string, dimKey: string): number | null => {
    const hit = cellValue.get(`${layerKey}|${dimKey}`);
    return hit === undefined ? null : hit;
  };

  /** 数值 → 颜色深浅：透明度随数值线性提升，高分格改深色字，保证 50+ 用户可读。 */
  function cellStyle(value: number, layerColorKey: string) {
    const base = resolveColor(layerColorKey);
    const alpha = 0.12 + (value / 100) * 0.55;
    return {
      background: `${base}${String(Math.round(alpha * 255)).padStart(2, '0')}`,
      borderColor: `${base}55`,
      color: value > 70 ? '#0A1020' : 'var(--text)',
    };
  }

  /** 参考线：各维度在三层上的平均分，用于对比「当前层 vs 全周期均值」。 */
  const refByDim = dimensions.map((dim) => {
    const values = matrix.filter((c) => c.dimKey === dim.key).map((c) => c.value);
    if (values.length === 0) return 0;
    return Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);
  });

  return (
    <div className="page">
      {/* ---------------- 返回头 ---------------- */}
      <div className="scr-head">
        <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
          <svg viewBox="0 0 24 24">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <div className="scr-title">
          传承学院<small>三层五维 · 战略性培养工程</small>
        </div>
      </div>

      {/* ---------------- 三层五维矩阵 ---------------- */}
      <Card
        title="三层五维培养矩阵"
        more={<span className="card-more">格内为完成度（%）</span>}
      >
        {matrix.length === 0 ? (
          <StateBox
            kind="empty"
            title="尚未建立培养矩阵"
            desc="完成家庭培养评估后，三层五维的完成度会在此逐格呈现。"
          />
        ) : (
          <>
            <div className="matrix">
              <div />
              {dimensions.map((dim) => (
                <div key={dim.key} className="mx-h" style={{ fontSize: MATRIX_KEY_MIN_FONT }}>
                  {dim.name}
                </div>
              ))}
              {orderedLayers.map((layer) => (
                <div key={layer.key} style={{ display: 'contents' }}>
                  <div className="mx-rh" style={{ color: resolveColor(layer.color), fontSize: MATRIX_KEY_MIN_FONT }}>
                    {layer.name}
                  </div>
                  {dimensions.map((dim) => {
                    const value = valueOf(layer.key, dim.key);
                    return (
                      <div
                        key={`${layer.key}-${dim.key}`}
                        className="mx-c"
                        style={{
                          fontSize: MATRIX_KEY_MIN_FONT,
                          ...(value === null ? { color: 'var(--muted-2)' } : cellStyle(value, layer.color)),
                        }}
                        title={value === null ? '暂无评估值' : `${layer.name} · ${dim.name} ${value}%，${levelOf(value)}`}
                      >
                        {value === null ? '—' : value}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="divider" />

            <div className="sec-title" style={{ marginTop: 4 }}>
              五个维度
            </div>
            <div className="text-tiny text-muted" style={{ lineHeight: 1.8 }}>
              {dimensions.map((dim, i) => (
                <span key={dim.key}>
                  {dim.name}
                  <span className="mono-num" style={{ color: 'var(--muted-2)' }}>
                    {' '}
                    {refByDim[i]}
                  </span>
                  {i < dimensions.length - 1 ? ' · ' : ''}
                </span>
              ))}
              <span style={{ color: 'var(--muted-2)' }}>（数值为全周期各维度均值）</span>
            </div>

            <div className="sp12" />

            <div className="sec-title" style={{ marginTop: 0 }}>
              三个层级
            </div>
            {orderedLayers.map((layer) => (
              <div key={layer.key} style={{ padding: '9px 0', borderBottom: '1px solid var(--line)' }}>
                <div className="row-flex" style={{ gap: 8, alignItems: 'baseline' }}>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: resolveColor(layer.color),
                      display: 'inline-block',
                      flex: '0 0 auto',
                    }}
                  />
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>{layer.name}</span>
                  <span className="text-tiny text-muted">{layer.sub}</span>
                </div>
                <div className="text-tiny text-muted" style={{ marginTop: 5, paddingLeft: 14, lineHeight: 1.7 }}>
                  {layer.goal}
                </div>
              </div>
            ))}

            <div className="note-src">
              <b>读法</b>：横轴为认知 · 品格 · 能力 · 经验 · 网络五维素养，纵轴为基石养成层 → 能力锻造层 → 战略目标层；
              格内数字为该项当前完成度，颜色越深完成度越高。
            </div>
          </>
        )}
      </Card>

      {/* ---------------- 接班人成长仪表盘 ---------------- */}
      <Card title="接班人成长仪表盘" more={<span className="card-more">共 {successors.length} 位在册</span>}>
        {successors.length === 0 ? (
          <StateBox kind="empty" title="暂无接班人档案" desc="建立接班人档案后，五维雷达与成长进度会在此呈现。" />
        ) : (
          <div className="stack">
            {successors.map((successor) => (
              <div key={successor.id} className="card tight" style={{ background: 'rgba(255,255,255,.02)' }}>
                <div className="row between" style={{ alignItems: 'flex-start' }}>
                  <div className="grow">
                    <div className="li-t" style={{ fontSize: 14, fontWeight: 600 }}>
                      {successor.name}
                      <span className="text-tiny text-muted" style={{ marginLeft: 6, fontWeight: 400 }}>
                        {successor.age} 岁
                      </span>
                    </div>
                    <div className="text-tiny text-muted" style={{ marginTop: 4 }}>
                      当前层级：{successor.layerName}
                    </div>
                  </div>
                  <Badge tone="e">五维雷达</Badge>
                </div>

                <div className="row" style={{ gap: 14, marginTop: 10, alignItems: 'center' }}>
                  <div style={{ flex: '0 0 auto' }}>
                    {successor.dims.length >= 3 ? (
                      <Chart
                        html={charts.radar({
                          axes: successor.dims.map((d) => ({ label: d.label, value: d.value })),
                          size: 168,
                          color: 'e',
                        })}
                      />
                    ) : (
                      /* 维度不足 3 项时雷达无法成面，退化为逐项进度，信息不丢 */
                      <div style={{ width: 168 }}>
                        {successor.dims.map((d) => (
                          <div key={d.key} style={{ marginBottom: 6 }}>
                            <div className="text-tiny text-muted">
                              {d.label} <span className="mono-num">{d.value}</span>
                            </div>
                            <ProgressBar value={d.value} color={resolveColor('e')} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="grow">
                    <div className="text-tiny text-muted">综合完成度</div>
                    <div className="num" style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1, color: resolveColor('e') }}>
                      {successor.progress}
                      <span style={{ fontSize: 13, color: 'var(--muted)' }}>%</span>
                    </div>
                    <div style={{ marginTop: 9 }}>
                      <ProgressBar value={successor.progress} color={resolveColor('e')} />
                    </div>
                    <div className="text-tiny text-muted" style={{ marginTop: 10, lineHeight: 1.7 }}>
                      {successor.currentState}
                    </div>
                  </div>
                </div>

                <div className="data-tag" style={{ marginTop: 10 }}>
                  下一步：{successor.nextStep}
                  {successor.nextStepAt ? ` · 计划时间 ${formatDate(successor.nextStepAt)}` : ' · 待排期'}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ---------------- 教练能力支持 ---------------- */}
      <Card title="教练能力支持" more={<span className="card-more">赋能企业家本人</span>}>
        {resources.length === 0 ? (
          <StateBox kind="empty" title="暂无教练资源" desc="教练资源开通后会在此展示，用于把家长从焦虑转为有章法的教练。" />
        ) : (
          resources.map((resource) => (
            <div key={resource.id} className="list-item">
              <div className="li-main">
                <div className="li-t">{resource.name}</div>
                <div className="li-s">{resource.desc}</div>
              </div>
              <div className="li-r">
                <Badge tone="ghost">{resource.statusText}</Badge>
              </div>
            </div>
          ))
        )}
        <div className="note-src">
          <b>方法论</b>：平台让企业家从「焦虑的父母」转变为「有章法的教练」——
          评估诊断 → 路径规划 → 资源匹配 → 沟通建构 → 持续追踪与迭代。
        </div>
      </Card>

      {/* ---------------- 体系说明 ---------------- */}
      <div className="note-src">
        <b>持续追踪 · 定期复盘</b>
        ：本页是伴随成长的追踪体系，不是一次性测评。矩阵与仪表盘随培养进度更新，建议按季度与专属教练复盘一次，
        调整下阶段目标与资源投入；AI 只做辅助研判，最终培养方案由人类专家确认。
      </div>
    </div>
  );
}
